import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
  selector: 'app-grouped-chart',
  templateUrl: './grouped-chart.component.html',
  styleUrls: ['./grouped-chart.component.scss'],
})
export class GroupedChartComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() data: any[] = [];
  @Input() type: 'group' | 'groupStack' = 'group';

  @Input() colors = [
    '#003c30',
    '#01665e',
    '#35978f',
    '#80cdc1',
    '#c7eae5',
    '#e6f5d0',
    '#f6e8c3',
    '#dfc27d',
    '#bf812d',
    '#8c510a',
    '#543005', '#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b', '#ffffbf', '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'
];

  @ViewChild('groupedChartCanvas')
  groupedChartCanvas!: ElementRef<HTMLCanvasElement>;

  labels: string[] = [];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    stack?: string;
  }[] = [];

  stackedBarChart: any;
  @Input() isMonth = false;
  showChart = true;

  constructor() {}

  ngOnInit(): void {}


  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data?.currentValue){
      this.data = changes.data.currentValue;
      if (this.groupedChartCanvas?.nativeElement) {
      setTimeout(() => {
        this.makeChart(this.groupedChartCanvas.nativeElement.getContext('2d'));
      }, 100);
      }

    }
  }

  ngAfterViewInit(): void {
    if (this.groupedChartCanvas?.nativeElement) {
    setTimeout(() => {
      this.makeChart(this.groupedChartCanvas.nativeElement.getContext('2d'));
    }, 100);
    }
  }

  makeChart(ctx: CanvasRenderingContext2D | null): void {
    if (ctx) {
        this.prepareData().then((data) => {
          const allZeros = data.datasets.every((dataset: any) => dataset.data.every((value: any) => value === 0));
          this.showChart = allZeros ? false : true;
          if (!allZeros){
       Chart.register(ChartDataLabels);
       this.stackedBarChart = new Chart(ctx, {
        type: 'bar',
        data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
          },
          scales: {
            x: {
              title: {
                display: false,
                text: this.data[0]?.value_format === 'kwh' ? 'Energy' : !this.isMonth ? 'Year' : 'Month, Year'
              },
              stacked: this.type === 'groupStack', // Enable grouped bars
            },
            y: {
              title: {
                display: true,
                text: 'Value'
              },
              ticks: {
                precision: 0
              },
              stacked: this.type === 'groupStack', // Enable stacked bars
            },
          },
          plugins: {
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 15,
                boxWidth: 8,
                font: {
                  size: 11
                },
              },
            },
            datalabels: {
              display: true,
              color: '#fff',
              anchor: 'center', // Adjust the anchor position as needed
              align: 'center', // Adjust the align as needed
              offset: 0,
              rotation: -90, // rotating text
              font: {
                size: 9,
                weight: 'bold',
              },
              formatter: (value, context) => {
                if (!(value - Math.floor(value) !== 0)){
                  const formattedNumber = this.formatNumberWithCommas(value);
                  // You can customize the label content here
                  return formattedNumber;
                }
                return '';
              },
            },
          },
        },
      });
    }
    });
    }
  }

  formatNumberWithCommas(value: any): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

  async prepareData(): Promise<any> {
    let datasets: any[] = [];
    const labelObj: any = {};
    if (!this.type || this.type === 'group'){


        this.data.forEach((item: any) => {
          item.values.forEach((entry: any, index: number) => {
              if (!datasets[entry.financial_year]) {
                  datasets[entry.financial_year] = {
                      label: entry.financial_year,
                      backgroundColor : this.colors[index],
                      barThickness: 15,
                      maxBarThickness: 20,
                      data: [],
                      ...(item.values?.length > 1) && {
                        hidden: entry.financial_year !== '2023' || index !== 0 && entry.financial_year !== '2023'
                      },
                  };
              }
              datasets[entry.financial_year].data.push(parseInt(entry.total_count, 10));
          });
      });

      // Convert datasets object to array
        datasets = Object.values(datasets);

    } else if (this.type === 'groupStack') {
        this.data.forEach((ele: any, index: number) => {
            const stack = ele.question_label || `stack ${index}`;
            const dataArr: any[] = ele.short_key || ele.short_key_women || ele.short_key_men;
            dataArr.forEach((el: any, shortKeyIndex: number) => {
                const obj: any = {
                    backgroundColor : this.colors[shortKeyIndex],
                    stack
                };
                obj.label = this.firstLetterCaptial(el.question_label || el.name);
                const values = el.values.sort((a: any, b: any) => a.financial_year - b.financial_year);
                obj.data = values.map((item: any) => {
                    labelObj[item.financial_year] = 1;
                    return item.total_count;
                });
                obj.hidden = shortKeyIndex !== 0;
                obj.barThickness = 15;
                obj.maxBarThickness = 20;
                datasets.push(obj);
            });
        });
    }
    const labels =  this.data.map(item => item.question_label);
    return { labels, datasets };
  }
  firstLetterCaptial(value: string): string {
    if (value) {
      const sentence: any = value.split('_');
      return sentence
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return '';
  }
}
